import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './LandingPage.module.css';

const features = [
  {
    title: 'Just talk, and it plans',
    description:
      'Tell the AI your subject, grade, and goals. It asks the right follow-up questions and builds a complete lesson plan — no templates, no forms.',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80',
    alt: 'Teacher working with AI assistant',
  },
  {
    title: 'Standards built in, not bolted on',
    description:
      'Every lesson maps to Common Core standards automatically. Browse, search, and link 312 CCSS Math standards right from the editor.',
    image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&q=80',
    alt: 'Organized classroom with learning standards',
  },
  {
    title: 'Your calendar, your year',
    description:
      'Schedule lessons, mark holidays, and see your whole term at a glance. Print lessons or export your full class summary as a PDF.',
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&q=80',
    alt: 'Calendar and planner on a desk',
  },
];

const steps = [
  {
    number: 1,
    title: 'Describe your lesson',
    desc: "Tell the AI what you want to teach. A topic, a standard, a vague idea — it'll work with whatever you've got.",
  },
  {
    number: 2,
    title: 'Watch it come together',
    desc: 'The AI builds a complete plan with objectives, materials, procedures, and assessment. Edit anything inline.',
  },
  {
    number: 3,
    title: 'Schedule and teach',
    desc: "Drop it on your calendar and walk into class ready. That's it.",
  },
];

const LandingPage = () => {
  const { user } = useAuth();

  if (user) {
    return <Navigate to="/classes" replace />;
  }

  return (
    <div className={styles.container}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <img
            src="/logo-cropped.svg"
            alt="Teachio logo"
            className={styles.heroLogo}
          />
          <h1 className={styles.heroTitle}>
            Planning lessons shouldn't eat your{' '}
            <span className={styles.heroHighlight}>evenings</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Describe what you want to teach, and Teachio builds a complete,
            standards-aligned lesson plan while you chat. It's like having a
            planning partner who knows the standards by heart.
          </p>
          <Link to="/register" className={styles.primaryBtn}>
            Create Your Free Account
          </Link>
          <p className={styles.heroSignIn}>
            Already have an account?{' '}
            <Link to="/login" className={styles.heroSignInLink}>
              Sign in
            </Link>
          </p>
        </div>
        <div className={styles.heroImageWrap}>
          <img
            className={styles.heroImage}
            src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80"
            alt="Teacher working on a laptop in a bright classroom"
            loading="lazy"
          />
        </div>
      </section>

      {/* Features */}
      <section className={styles.features}>
        <div className={styles.featuresHeader}>
          <h2 className={styles.sectionTitle}>
            Everything you need in a modern plan book
          </h2>
        </div>
        <div className={styles.featureGrid}>
          {features.map((f) => (
            <div className={styles.featureCard} key={f.title}>
              <div className={styles.featureImageWrap}>
                <img
                  className={styles.featureImage}
                  src={f.image}
                  alt={f.alt}
                  loading="lazy"
                />
              </div>
              <h3 className={styles.featureCardTitle}>{f.title}</h3>
              <p className={styles.featureCardDesc}>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howItWorks}>
        <div className={styles.howItWorksInner}>
          <div className={styles.featuresHeader}>
            <h2 className={styles.sectionTitle}>How it works</h2>
          </div>
          <div className={styles.stepsGrid}>
            {steps.map((s) => (
              <div className={styles.step} key={s.number}>
                <div className={styles.stepNumber}>{s.number}</div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepDesc}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.finalCtaContent}>
          <h2 className={styles.finalCtaTitle}>
            Spend your time teaching, not planning
          </h2>
          <p className={styles.finalCtaSubtitle}>
            Free to use. No credit card required.
          </p>
          <Link to="/register" className={styles.ctaWhiteBtn}>
            Get Started
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <p className={styles.footerText}>
            Already have an account?{' '}
            <Link to="/login" className={styles.footerLink}>
              Sign in
            </Link>
          </p>
          <div className={styles.footerLinks}>
            <Link to="/privacy" className={styles.footerLink}>
              Privacy Policy
            </Link>
            <span className={styles.footerDivider}>&middot;</span>
            <Link to="/terms" className={styles.footerLink}>
              Terms of Service
            </Link>
            <span className={styles.footerDivider}>&middot;</span>
            <Link to="/cookies" className={styles.footerLink}>
              Cookie Policy
            </Link>
          </div>
          <p className={styles.footerCopyright}>
            &copy; 2026 REEC Holdings. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
